import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { capitalizeFirstLetter } from '../../../../../utils/commonFunctions';
import URLs from '../../../../../utils/appURLs';
import { updateToast } from '../../../../../common/redux/reducer/commonSlices/toastSlice';
import axiosInstance from '../../../../../app/config/axiosInstance';

type ReturnedType = any;

type ThunkArg = {
  id?: number;
  address?: string;
  company?: string;
  dba_Name?: string;
  contactNumber?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  roleType?: string;
  latitude?: string;
  longitude?: string;
};

interface Logistics {
  company: string;
  dba_Name: string;
  roleType: string;
  firstName: string;
  lastName: string;
  contactNumber: number;
  email: string;
  address: string;
  id: number;
  latitude: string;
  longitude: string;
}

const initialState: any = {
  isLoading: false,
  type: 'add',
  data: [],
  error: null,
  count: null
};

export const addLogistics = createAsyncThunk<ReturnedType, ThunkArg>(
  'user/logistics/add',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'POST',
        url: `${URLs.ADD_LOGISTICS}`,
        data
      });

      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response.data.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.error
            ? error?.response?.data?.error
            : error?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const getLogistics = createAsyncThunk<ReturnedType, any>(
  'api/logistics',
  async (data, thunkApi) => {
    try {
      const {
        pageNo = 1,
        pageSize = 5,
        sortColumnName,
        sortBy,
        searchInput,
        searchColumnName,
        roleType,
        signal: signal
      } = data;

      let url = `${URLs.GET_ALL_LOGISTICS}?pageNo=${pageNo}&pageSize=${pageSize}`;

      if (roleType && roleType !== 'All') {
        url += `&roletype=${roleType}`;
      }
      if (sortBy && sortColumnName && searchColumnName && searchInput) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}&searchInput=${searchInput}&searchColumnName=${searchColumnName}`;
      } else if (sortBy && sortColumnName && searchInput) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}&searchInput=${searchInput}`;
      } else if (searchInput && searchColumnName) {
        url += `&searchInput=${searchInput}&searchColumnName=${searchColumnName}`;
      } else if (sortBy && sortColumnName && searchInput && searchColumnName) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}&searchInput=${searchInput}&searchColumnName=${searchColumnName}`;
      } else if (sortBy && sortColumnName && searchInput) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}&searchInput=${searchInput}`;
      } else if (searchInput) {
        url += `&searchInput=${searchInput}`;
      } else if (sortBy && sortColumnName) {
        url += `&sortBy=${sortBy}&sortColumnName=${sortColumnName}`;
      }
      const response = await axiosInstance({
        method: 'GET',
        url: url,
        signal: signal
      });
      return response?.data;
    } catch (error: any) {
      if (error?.message !== 'canceled') {
        thunkApi.dispatch(
          updateToast({
            show: true,
            message: error?.response?.data?.message
              ? error?.response?.data?.message
              : error?.message,
            severity: 'error'
          })
        );
      }
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const updateLogistics = createAsyncThunk<ReturnedType, any>(
  'api/update',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'PUT',
        url: `${URLs.UPDATE_LOGISTICS}?id=${data.id}`,
        data: data.updatePayload
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.error
            ? error?.response?.data?.error
            : error?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const deleteLogistics = createAsyncThunk<ReturnedType, ThunkArg>(
  'api/delete',
  async (data, thunkApi) => {
    const { id, roleType } = data;
    try {
      const response = await axiosInstance({
        method: 'DELETE',
        url: `${URLs.DELETE_LOGISTICS}?id=${id}`
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );

      return response?.data?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.error
            ? error?.response?.data?.error
            : error?.response?.data?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

export const deactiveLogistics = createAsyncThunk<ReturnedType, ThunkArg>(
  'api/deactivate',
  async (data, thunkApi) => {
    const { id, roleType } = data;
    try {
      const response = await axiosInstance({
        method: 'PATCH',
        url: `${URLs.DEACTIVATE_LOGISTICS}?id=${id}`
      });
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );

      return response?.data?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error?.response?.data?.error
            ? error?.response?.data?.error
            : error?.message,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

const LogisticsSlice = createSlice({
  name: 'logistics',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      // get logistics
      .addCase(getLogistics.pending, state => {
        state.isLoading = true;
        state.data = [];
        state.type = 'logistics';
      })
      .addCase(getLogistics.fulfilled, (state, action: any) => {
        state.isLoading = false;
        const {
          data: { stakeholders, stakeholdersCount }
        } = action.payload;
        state.data = stakeholders.map((item: any, index: number) => ({
          id: item.id,
          company: capitalizeFirstLetter(item?.company),
          dba_Name: capitalizeFirstLetter(item?.DbaName),
          roleType: capitalizeFirstLetter(item?.roleType),
          firstName: capitalizeFirstLetter(item?.firstName),
          lastName: capitalizeFirstLetter(item?.lastName),
          contactNumber: item?.contactNumber,
          email: item?.email,
          stakeholderId: item.stakeholderId.toUpperCase(),
          address: item.address,
          isActive: item.isActive,
          latitude: item?.latitude,
          longitude: item?.longitude,
          type: 'Logistics'
        }));
        state.type = 'logistics';
        state.count = stakeholdersCount;
        state.error = null;
      })
      .addCase(getLogistics.rejected, (state, action: any) => {
        state.isLoading = false;
        state.data = [];
        state.type = 'logistics';
        state.error = action.payload?.response?.data.error || 'Something went wrong';
      });

    // add logistics
    builder.addCase(addLogistics.pending, state => {
      state.isLoading = true;
      state.type = 'add';
    });
    builder.addCase(addLogistics.fulfilled, (state, action: any) => {
      state.data = action?.payload?.data;

      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(addLogistics.rejected, (state, action: any) => {
      state.isLoading = false;
      state.error = action?.error?.message || 'Something went wrong';
    });

    // update logistics
    builder.addCase(updateLogistics.pending, state => {
      state.isLoading = true;
      state.type = 'update';
    });
    builder.addCase(
      updateLogistics.fulfilled,
      (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.data = action?.payload?.data;
        state.error = null;
      }
    );
    builder.addCase(updateLogistics.rejected, (state, action: any) => {
      state.isLoading = false;
      state.error = action?.payload?.response?.data?.error || 'Something went wrong';
    });

    // deactive logistics
    builder.addCase(deactiveLogistics.pending, state => {
      state.isLoading = true;
      state.error = null;
      state.type = 'deactivate';
    });
    builder.addCase(
      deactiveLogistics.fulfilled,
      (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.data = action?.payload;
        state.error = null;
      }
    );
    builder.addCase(deactiveLogistics.rejected, (state, action: any) => {
      state.isLoading = false;
      state.error = action?.payload?.response?.data?.error || 'Something went wrong';
    });

    // delete logistics
    builder.addCase(deleteLogistics.pending, state => {
      state.isLoading = true;
      state.error = null;
      state.type = 'delete';
    });
    builder.addCase(
      deleteLogistics.fulfilled,
      (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.data = state?.data?.filter((row: any) => row?.id !== action?.payload?.id);
        state.error = null;
      }
    );
    builder.addCase(deleteLogistics.rejected, (state, action: any) => {
      state.isLoading = false;
      state.error = action.payload?.response?.data?.error || 'Something went wrong';
    });
  }
});

export default LogisticsSlice.reducer;
